/**
 * Placeholder extraction — finds {{TOKEN}} in a document and reports what a
 * later bulk-replacement pass would have to fill in.
 *
 * This phase only detects and reports. Replacement is a later phase, which is
 * why a variable carries the element ids that reference it: whoever implements
 * replacement should not have to re-walk the document to find them.
 */

import { VARIABLE_TOKENS } from './constants';

/**
 * Deliberately not anchored to the known-token list: an unknown token is
 * extracted and warned about, not ignored. Whitespace inside the braces is
 * tolerated because hand-written JSON collects it.
 */
const TOKEN_PATTERN = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

const KNOWN = new Set(VARIABLE_TOKENS);

/** TITLE → Title, CONTACT_PHONE → Contact Phone. */
const humanize = (token) =>
  token
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const tokensIn = (text) => {
  const found = [];
  if (typeof text !== 'string') return found;
  // Fresh lastIndex per call — TOKEN_PATTERN is global and module-scoped.
  TOKEN_PATTERN.lastIndex = 0;
  let match = TOKEN_PATTERN.exec(text);
  while (match) {
    found.push(match[1]);
    match = TOKEN_PATTERN.exec(text);
  }
  return found;
};

/**
 * Scan every page for placeholders.
 *
 * A token found anywhere in an image `src` is reported as `kind: 'image'`, even
 * if the same token also appears in a text element: an image slot has to be
 * filled with a URL, and that is the stricter of the two requirements.
 *
 * @param {Object} document
 * @returns {{variables: import('./types').Variable[], warnings: string[]}}
 */
export const extractVariables = (document) => {
  const pages = (document && Array.isArray(document.pages)) ? document.pages : [];

  /** @type {Map<string, {token: string, kind: string, elementIds: string[]}>} */
  const byToken = new Map();

  const record = (token, kind, elementId) => {
    const existing = byToken.get(token);
    if (!existing) {
      byToken.set(token, { token, kind, elementIds: elementId ? [elementId] : [] });
      return;
    }
    if (kind === 'image') existing.kind = 'image';
    if (elementId && !existing.elementIds.includes(elementId)) existing.elementIds.push(elementId);
  };

  pages.forEach((page) => {
    const elements = (page && Array.isArray(page.elements)) ? page.elements : [];
    elements.forEach((el) => {
      if (!el) return;
      if (el.type === 'image') {
        tokensIn(el.src).forEach((token) => record(token, 'image', el.id));
      }
      tokensIn(el.text).forEach((token) => record(token, 'text', el.id));
    });
  });

  const variables = [];
  const warnings = [];

  byToken.forEach(({ token, kind, elementIds }) => {
    if (!KNOWN.has(token.toUpperCase())) {
      warnings.push(`Unrecognized placeholder {{${token}}} — it will still be imported as a field`);
    }
    variables.push({
      token,
      name: humanize(token),
      kind,
      elementIds,
      defaultValue: `{{${token}}}`,
    });
  });

  return { variables, warnings };
};
